import React, { forwardRef, useContext } from "react";
import classNames from "classnames";
import ResponsiveImageContext from "../../context/ResponsiveImageContext";
import { getImagePath } from "../../utilities/path";
import IEditableProps from "../../utilities/IEditableProps";
import IStyledProps from "../../utilities/IStyledProps";
import { getWebpUrl } from "../Picture/webpUtils";

interface ImageData {
    extension: string;
    size: number;
    density: number;
}

interface ImageAggregatedDensitites {
    extension: string;
    size: number;
    densities: number[];
}

/**
 * Extracts the base name of the image from the full image path.
 * Example:
 *   Input: "https://www.macu.com/media/path/image-640@1x.jpg"
 *   Output: "path/image"
 * @param fullImage - The full image path.
 * @returns The base name of the image or null if not found.
 */
function getBaseName(fullImage: string) {
    const relativeImage = fullImage.replace(process.env.GATSBY_MEDIA_URL, '').replace(/^\/(media\/)?/,'');
    const responsiveImageRegex = /(.*)-(\d+)@([1|2])x\.(jpg|jpeg|png|gif|webp|svg)$/i;
    const responsiveMatch = relativeImage.match(responsiveImageRegex);
    if (!responsiveMatch) {
        const regularImageRegex = /(.*)\.(jpg|jpeg|png|gif|webp|svg)$/i;
        const regularMatch = relativeImage.match(regularImageRegex);
        if (!regularMatch) return null;
        return regularMatch[1]; // Return the base name without the extension
    }
    return responsiveMatch[1];
}

/** * Extracts image data from the filename, including size, density, and extension.
 * Example:
 *   Input: "640@1x.jpg"
 *   Output: { size: 640, density: 1, extension: "jpg" }
 */
function getImageDataFromFilename(image: string): ImageData {
    if (image.indexOf('@') === -1) {
        // If the image does not contain '@', it is a regular image without size/density
        return { size: 0, density: 0, extension: image }; // Default values if no image is provided
    }
    const [size, rest] = image.split('@');
    const [density, extension] = rest.split('x.');
    return { size: Number(size), density: Number(density), extension };
}

/**
 * Aggregates the pixel densities of the images.
 * Example:
 *   Input: [{ size: 640, density: 1, extension: "jpg" }, { size: 640, density: 2, extension: "jpg" }]
 *   Output: [{ size: 640, densities: [1, 2], extension: "jpg" }]
 * @param images - The array of images with density aggregated in an array.
 * @returns An array of aggregated image data.
 */
function aggregatePixelDensity(images: ImageData[]): ImageAggregatedDensitites[] {
    const groupedImages = [];
    images.forEach(image => {
        const { extension, size, density } = image;
        const existingGroup = groupedImages.find(group => group.extension === extension && group.size === size);
        if (existingGroup) {
            existingGroup.densities.push(density);
        } else {
            groupedImages.push({ extension, size, densities: [density] });
        }
    });
    return groupedImages;
}

/**
 * ResponsivePicture component renders a responsive image using the <picture> element.
 * It uses the ResponsiveImageContext to get the responsive images available, this context is populated in Gatsby but empty in Buildr
 * It generates <source> elements for different resolutions and a fallback <img> element.
 */
export interface IResponsivePictureProps extends IEditableProps, IStyledProps {
    className?: string;
    pictureClassName?: string;
    id?: string;
    src: string;
    alt: string;
    dataTestId?: string;
    imgRef?: React.Ref<HTMLImageElement>;
    loading?: "lazy" | "eager";
}

const ResponsivePicture = forwardRef((props: IResponsivePictureProps, ref: any) => {
    const {src = "", alt = "", id = "", dataTestId, pictureClassName = "", className = "", imgRef} = props;

    if (!src) {
        return null; // If no src is provided, return null to avoid rendering an empty picture element
    }

    const baseImage = getBaseName(src);
    const responsiveImages = useContext(ResponsiveImageContext);
    const imageVersions = responsiveImages[baseImage] || [];
    const imageVersionData = aggregatePixelDensity(imageVersions.map((version) => getImageDataFromFilename(version)).sort((a, b) => {
        if (a.size === b.size) {
            return a.extension.toLowerCase() === 'webp' ? -1 : 1; // Prioritize webp over other formats
        }
        return a.size - b.size;
    }));

    // Image with size 0 in the name is the largest and should be used as the default
    const allSizes = imageVersionData.map(data => data.size);
    const largestSize = allSizes.includes(0) ? 0 : Math.max(...allSizes);

    // Default regular image is the largest size (image with size 0 or largest size) with a non-webp extension
    const defaultRegularImage = imageVersionData.find(data => data.size === 0 && data.extension.toLowerCase() !== 'webp')
        || imageVersionData.find(data => data.size === largestSize && data.extension.toLowerCase() !== 'webp')
        || null;

    // Default webp image is an image with size 0 in the name and extension 'webp', no need to find the largest size because it's already rendered as a source
    const defaultWebpImage = imageVersionData.find(data => data.size === 0 && data.extension.toLowerCase() === 'webp')
        || null;

        return (
        <picture className={classNames(pictureClassName)} data-test-id={dataTestId} ref={ref}>
            {imageVersionData.filter((data) => data.size > 0).map((data, index) => {
                const { size, densities, extension } = data;
                const imageNames = densities.map(density => `${getImagePath(baseImage)}-${size}@${density}x.${extension}${density > 1 ? ` ${density}x` : ''}`);
                return (
                    <source key={`source-${index}`} srcSet={imageNames.join(", ")} media={size === largestSize ? undefined : `(max-width: ${size}px)`} type={extension.toLowerCase() === "webp" ? "image/webp" : undefined} data-test-id={dataTestId && `${dataTestId}_source_${index}`}/>
                );
            })}
            {imageVersions.length === 0 ? <source key={`source-webp`} srcSet={getWebpUrl(getImagePath(src))} type={"image/webp"} data-test-id={dataTestId && `${dataTestId}_source_webp`}/> : null}
            {defaultWebpImage ? <source key={`source-webp`} srcSet={getImagePath(`${baseImage}.${defaultWebpImage.extension}`)} type={"image/webp"} data-test-id={dataTestId && `${dataTestId}_source_webp`}/> : null}
            <img
                src={defaultRegularImage ? getImagePath(defaultRegularImage.size ? `${baseImage}-${defaultRegularImage.size}@${defaultRegularImage.densities[0]}x.${defaultRegularImage.extension}` : `${baseImage}.${defaultRegularImage.extension}`) : getImagePath(src)}
                alt={alt}
                id={id ? id : undefined}
                ref={imgRef}
                className={classNames(className)}
                loading={props.loading}
                data-test-id={dataTestId && `${dataTestId}_img`}
                />
        </picture>
    );
});

export default ResponsivePicture;